import { evaluateExpression } from '../shared';
import type { PlotSpec, PlotTrace } from '../shared';
import type { FragmentTrajectoryResultState } from './appConfig';
import type { ParsedValues } from './validation';
import { parseLaunchAngles, parseTarget } from './trajectory';
import type { ImpactPoint, TrajectoryAnalysis, TrajectoryInput } from './trajectory';
import trajectoryWorkerUrl from './trajectory.worker.ts?worker&url';

type WorkerResponse = { ok: true; result: TrajectoryAnalysis } | { ok: false; error: string };

const NOMINAL_COLOR = '#2563eb';
const DESIGN_COLOR = '#dc2626';
const TARGET_COLOR = '#475569';

function numberFromExpression(value: string, label: string): number {
  const number = evaluateExpression(value);
  if (number === null || !Number.isFinite(number)) throw new Error(`${label} is not a valid number.`);
  return number;
}

function mapValuesToInput(values: ParsedValues): TrajectoryInput {
  return {
    airDensityLbMs2PerIn4: numberFromExpression(values.rho0, 'Air density'),
    launchHeightFt: numberFromExpression(values.h0, 'Launch height'),
    launchVelocityFtPerS: numberFromExpression(values.v0, 'Launch velocity'),
    launchAnglesDeg: parseLaunchAngles(values.angles),
    fragmentWeightLbf: numberFromExpression(values.Wt, 'Fragment weight'),
    dragAreaIn2: numberFromExpression(values.AD, 'Drag area'),
    dragCoefficient: numberFromExpression(values.CD, 'Drag coefficient'),
    distanceFactor: numberFromExpression(values.FS, 'DDESB distance factor'),
    target: parseTarget(values.target)
  };
}

function calculateInWorker(input: TrajectoryInput): Promise<TrajectoryAnalysis> {
  return new Promise((resolve, reject) => {
    const workerModuleUrl = new URL(trajectoryWorkerUrl, import.meta.url);
    let bootstrapUrl: string | null = null;
    let worker: Worker;

    if (workerModuleUrl.origin === window.location.origin) {
      worker = new Worker(workerModuleUrl, { type: 'module' });
    } else {
      // During development Django and Vite use different origins. A blob worker
      // inherits the page origin and can import Vite's CORS-enabled module.
      bootstrapUrl = URL.createObjectURL(new Blob([`import ${JSON.stringify(workerModuleUrl.href)};`], { type: 'text/javascript' }));
      worker = new Worker(bootstrapUrl, { type: 'module' });
    }

    const timeout = window.setTimeout(() => {
      finish();
      reject(new Error('The trajectory calculation exceeded 60 seconds. Reduce the number of angles or revise the inputs.'));
    }, 60_000);

    function finish() {
      window.clearTimeout(timeout);
      worker.terminate();
      if (bootstrapUrl) URL.revokeObjectURL(bootstrapUrl);
    }

    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      finish();
      if (event.data.ok) resolve(event.data.result);
      else reject(new Error(event.data.error));
    };
    worker.onerror = event => {
      finish();
      reject(new Error(event.message || 'The fragment trajectory worker failed.'));
    };
    worker.postMessage(input);
  });
}

function impactTrace(points: readonly ImpactPoint[], name: string, color: string, symbol: 'circle' | 'star'): PlotTrace | null {
  if (points.length === 0) return null;
  return {
    name,
    x: points.map(point => point.xFt),
    y: points.map(point => point.zFt),
    mode: 'markers',
    color,
    marker: { size: 9, symbol, color, lineColor: '#ffffff', lineWidth: 1 },
    customData: points.map(point => [point.angleDeg, point.velocityFtPerS, point.momentumLbfS, point.timeS]),
    hoverTemplate: 'Angle: %{customdata[0]:.2f}°<br>x: %{x:.2f} ft<br>z: %{y:.2f} ft<br>Impact v: %{customdata[1]:.2f} ft/s<br>Momentum: %{customdata[2]:.2f} lbf·s<br>Time: %{customdata[3]:.3f} s<extra></extra>'
  };
}

function buildPlot(analysis: TrajectoryAnalysis, input: TrajectoryInput): PlotSpec {
  const traces: PlotTrace[] = [];
  analysis.nominal.trajectories.forEach((trajectory, index) => {
    traces.push({
      name: 'Nominal trajectories',
      x: trajectory.xFt,
      y: trajectory.zFt,
      mode: 'lines',
      color: NOMINAL_COLOR,
      lineWidth: 1,
      opacity: 0.45,
      legendGroup: 'nominal',
      showLegend: index === 0,
      customData: trajectory.vxFtPerS.map((vx, pointIndex) => [Math.hypot(vx, trajectory.vzFtPerS[pointIndex]), trajectory.angleDeg, 'Nominal']),
      hoverTemplate: '%{customdata[2]}<br>Angle: %{customdata[1]:.2f}°<br>x: %{x:.2f} ft<br>z: %{y:.2f} ft<br>v: %{customdata[0]:.2f} ft/s<extra></extra>'
    });
  });
  analysis.design.trajectories.forEach((trajectory, index) => {
    traces.push({
      name: 'DDESB design trajectories',
      x: trajectory.xFt,
      y: trajectory.zFt,
      mode: 'lines',
      color: DESIGN_COLOR,
      lineStyle: 'dash',
      lineWidth: 1.5,
      opacity: 0.9,
      legendGroup: 'design',
      showLegend: index === 0,
      customData: trajectory.vxFtPerS.map((vx, pointIndex) => [Math.hypot(vx, trajectory.vzFtPerS[pointIndex]), trajectory.angleDeg, 'DDESB design']),
      hoverTemplate: '%{customdata[2]}<br>Angle: %{customdata[1]:.2f}°<br>x: %{x:.2f} ft<br>z: %{y:.2f} ft<br>v: %{customdata[0]:.2f} ft/s<extra></extra>'
    });
  });

  if (input.target.kind === 'rectangle') {
    const x0 = input.target.standoffFt;
    const x1 = x0 + input.target.widthFt;
    traces.unshift({
      name: 'Target outline',
      x: [x0, x0, x1, x1, x0],
      y: [0, input.target.heightFt, input.target.heightFt, 0, 0],
      mode: 'lines',
      color: TARGET_COLOR,
      lineWidth: 2,
      fill: 'toself',
      opacity: 0.25,
      hoverTemplate: 'Target outline<extra></extra>'
    });
  }

  const nominalImpacts = impactTrace(analysis.nominal.impacts, 'Nominal impact points', '#1d4ed8', 'circle');
  const designImpacts = impactTrace(analysis.design.impacts, 'DDESB design impact points', '#b91c1c', 'star');
  if (nominalImpacts) traces.push(nominalImpacts);
  if (designImpacts) traces.push(designImpacts);

  const maximumHeightFt = Math.max(analysis.nominal.maximumHeightFt, analysis.design.maximumHeightFt, input.target.kind === 'rectangle' ? input.target.heightFt : 0, 1);
  const maximumXFt = Math.max(analysis.nominal.maximumRangeFt, analysis.design.maximumRangeFt, analysis.specifiedDesignRangeFt, input.target.standoffFt + (input.target.kind === 'rectangle' ? input.target.widthFt : 0), 1);
  const shapes: PlotSpec['shapes'] = [
    {
      kind: 'vline',
      x: analysis.specifiedDesignRangeFt,
      y0: 0,
      y1: maximumHeightFt,
      color: '#7c3aed',
      lineStyle: 'dot',
      lineWidth: 2,
      label: `DDESB factored range (${analysis.distanceFactor.toFixed(2)}×)`
    }
  ];
  if (input.target.kind === 'plane') shapes.unshift({ kind: 'vline', x: input.target.standoffFt, y0: 0, y1: maximumHeightFt, color: TARGET_COLOR, lineWidth: 3, label: 'Target' });

  return {
    title: `Fragment Trajectories: nominal v₀=${analysis.nominal.launchVelocityFtPerS.toFixed(1)} ft/s; DDESB equivalent v₀=${analysis.design.launchVelocityFtPerS.toFixed(1)} ft/s`,
    xLabel: 'Standoff (ft)',
    yLabel: 'Height (ft)',
    xRange: [0, maximumXFt * 1.05],
    yRange: [0, maximumHeightFt * 1.05],
    traces,
    shapes
  };
}

function maximumImpactVelocity(points: readonly ImpactPoint[]): number | null {
  return points.length > 0 ? Math.max(...points.map(point => point.velocityFtPerS)) : null;
}

function maximumImpactMomentum(points: readonly ImpactPoint[]): number | null {
  return points.length > 0 ? Math.max(...points.map(point => point.momentumLbfS)) : null;
}

function summaryRow(label: string, value: string): string {
  return `<div class="summary-row"><span class="summary-label">${label}:</span><span class="summary-value">${value}</span></div>`;
}

function escapeHtml(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;').replaceAll("'", '&#39;');
}

function buildSummary(input: TrajectoryInput, angleSource: string, analysis: TrajectoryAnalysis): { html: string } {
  const nominalImpactVelocity = maximumImpactVelocity(analysis.nominal.impacts);
  const designImpactVelocity = maximumImpactVelocity(analysis.design.impacts);
  const designImpactMomentum = maximumImpactMomentum(analysis.design.impacts);
  const inputRows = [
    summaryRow('Air density', `${input.airDensityLbMs2PerIn4} lbf·ms²/in⁴`),
    summaryRow('Launch height', `${input.launchHeightFt} ft`),
    summaryRow('Launch velocity', `${input.launchVelocityFtPerS} ft/s`),
    summaryRow('Launch angles (°)', escapeHtml(angleSource)),
    summaryRow('Fragment weight', `${input.fragmentWeightLbf} lbf`),
    summaryRow('Drag area', `${input.dragAreaIn2} in²`),
    summaryRow('Drag coefficient', `${input.dragCoefficient}`),
    summaryRow('DDESB distance factor', `${input.distanceFactor}`),
    summaryRow('Target standoff', `${input.target.standoffFt} ft`),
    ...(input.target.kind === 'rectangle' ? [summaryRow('Target height', `${input.target.heightFt} ft`), summaryRow('Target width', `${input.target.widthFt} ft`)] : [])
  ];
  const rows = [
    summaryRow('Nominal launch velocity', `${analysis.nominal.launchVelocityFtPerS.toFixed(1)} ft/s`),
    summaryRow('Nominal maximum range', `${analysis.nominal.maximumRangeFt.toFixed(1)} ft`),
    summaryRow('DDESB distance factor', analysis.distanceFactor.toFixed(2)),
    summaryRow('DDESB design range', `${analysis.specifiedDesignRangeFt.toFixed(1)} ft`),
    summaryRow('Equivalent design launch velocity', `${analysis.design.launchVelocityFtPerS.toFixed(1)} ft/s`),
    summaryRow('Equivalent velocity factor', analysis.equivalentVelocityFactor.toFixed(4)),
    summaryRow('Calculated design maximum range', `${analysis.design.maximumRangeFt.toFixed(1)} ft`),
    nominalImpactVelocity === null ? summaryRow('Nominal target status', 'No nominal trajectory intersects the target') : summaryRow('Maximum nominal impact velocity', `${nominalImpactVelocity.toFixed(1)} ft/s`),
    designImpactVelocity === null ? summaryRow('DDESB design target status', 'No design trajectory intersects the target') : summaryRow('Maximum DDESB design impact velocity', `${designImpactVelocity.toFixed(1)} ft/s`),
    ...(designImpactMomentum === null ? [] : [summaryRow('Maximum DDESB design impact momentum', `${designImpactMomentum.toFixed(1)} lbf·s`)]),
    ...analysis.warnings.map(warning => `<div class="summary-note summary-note--warning"><strong>Warning:</strong> ${warning}</div>`),
    '<div class="summary-note">The TP-13 factor is a distance factor. The equivalent design launch velocity is an engineering transformation used to create a dynamically consistent factored trajectory and estimate impact velocity; TP-13 does not prescribe a velocity multiplier.</div>'
  ];
  return {
    html: `<div class="summary-report summary-report--cols-1"><div class="summary-group"><div class="summary-section"><strong>Fragment Trajectory Inputs</strong></div><div class="summary-separator">--------------------------------------</div>${inputRows.join('')}</div><div class="summary-group"><div class="summary-section"><strong>Fragment Trajectory Results</strong></div><div class="summary-separator">--------------------------------------</div>${rows.join('')}</div></div>`
  };
}

export async function analyze(values: ParsedValues): Promise<FragmentTrajectoryResultState> {
  const input = mapValuesToInput(values);
  const analysis = await calculateInWorker(input);
  return { analysis, summary: buildSummary(input, values.angles, analysis), plotData: { trajectory: buildPlot(analysis, input) } };
}
